// NOTICE: KAPTION-INTEGRATION
// Composable for Undo Send feature - handles hotkeys, page leave warnings, and undo state
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { emitter } from 'shared/helpers/mitt';

// Event name for restoring content to ReplyBox
export const UNDO_RESTORE_CONTENT_EVENT = 'kaption:undo-restore-content';

/**
 * Composable for managing the Undo Send feature
 * Provides:
 * - undoModeEnabled: computed property indicating if undo mode is enabled
 * - hasUndoPending: computed property indicating if there are pending messages
 * - undoLastMessage: function to undo the most recent pending message
 * - setupUndoHotkeys: function to setup Escape/Ctrl+Z hotkeys
 * - cleanupUndoHotkeys: function to remove hotkey listeners
 *
 * @returns {Object} Undo send utilities
 */
export function useUndoSend() {
  const store = useStore();
  const { uiSettings } = useUISettings();

  // Track if hotkeys are set up
  const hotkeysActive = ref(false);

  // Check if undo mode is enabled in settings
  const undoModeEnabled = computed(
    () => uiSettings.value?.kaption_undo_send_enabled ?? false
  );

  // Check if there are any pending undo messages
  // Use direct store getter access for reliability
  const hasUndoPending = computed(
    () => store.getters.hasUndoPendingMessages ?? false
  );

  // Get all pending undo messages
  const undoPendingMessages = computed(
    () => store.getters.getUndoPendingMessages ?? {}
  );

  // Get the most recent pending message ID
  const lastPendingMessageId = computed(
    () => store.getters.getLastUndoPendingMessageId ?? null
  );

  /**
   * Undo the most recent pending message
   * @returns {Promise<Object|null>} The restored message payload or null
   */
  const undoLastMessage = async () => {
    const result = await store.dispatch('undoLastMessage');
    if (result) {
      // Emit event to restore content to ReplyBox
      emitter.emit(UNDO_RESTORE_CONTENT_EVENT, {
        content: result.content,
        attachments: result.attachments || [],
        conversationId: result.conversation_id,
      });
    }
    return result;
  };

  /**
   * Undo a specific pending message by tempId
   * @param {string} tempId - The temporary message ID
   * @returns {Promise<Object|null>} The restored message payload or null
   */
  const undoMessage = async tempId => {
    const result = await store.dispatch('undoMessage', tempId);
    if (result) {
      // Emit event to restore content to ReplyBox
      emitter.emit(UNDO_RESTORE_CONTENT_EVENT, {
        content: result.content,
        attachments: result.attachments || [],
        conversationId: result.conversation_id,
      });
    }
    return result;
  };

  /**
   * Create a message with undo capability
   * @param {Object} messageData - The message data (content, conversationId, etc.)
   * @returns {Promise<Object>} { tempId, undo: Function }
   */
  const sendWithUndo = async messageData => {
    return store.dispatch('createMessageWithUndo', messageData);
  };

  // Hotkey handler for Escape and Ctrl+Z/Cmd+Z
  const handleKeyDown = event => {
    // Only act if undo mode is enabled and there are pending messages
    if (!undoModeEnabled.value || !hasUndoPending.value) return;

    // Check if we're in an input/textarea - allow Escape but not Ctrl+Z in inputs
    const isInInput =
      event.target.tagName === 'INPUT' ||
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable;

    // Escape key - always works (most intuitive "cancel" action)
    if (event.key === 'Escape') {
      event.preventDefault();
      undoLastMessage();
      return;
    }

    // Ctrl+Z / Cmd+Z - only works when not in an input (to avoid conflicting with text undo)
    const isUndoHotkey =
      (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey;

    if (isUndoHotkey && !isInInput) {
      event.preventDefault();
      undoLastMessage();
    }
  };

  // Page leave warning handler
  const handleBeforeUnload = event => {
    if (hasUndoPending.value) {
      event.preventDefault();
      // Modern browsers ignore custom messages but still show a generic warning
      event.returnValue =
        'You have unsent messages. Are you sure you want to leave?';
      return event.returnValue;
    }
    return undefined;
  };

  /**
   * Setup hotkey listeners for undo
   */
  const setupUndoHotkeys = () => {
    if (hotkeysActive.value) return;

    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);
    hotkeysActive.value = true;
  };

  /**
   * Cleanup hotkey listeners
   */
  const cleanupUndoHotkeys = () => {
    document.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    hotkeysActive.value = false;
  };

  // Auto-setup and cleanup when used in a component
  onMounted(() => {
    if (undoModeEnabled.value) {
      setupUndoHotkeys();
    }
  });

  onUnmounted(() => {
    cleanupUndoHotkeys();
  });

  // Watch for changes in undoModeEnabled and setup/cleanup hotkeys accordingly
  watch(undoModeEnabled, newValue => {
    if (newValue) {
      setupUndoHotkeys();
    } else {
      cleanupUndoHotkeys();
    }
  });

  return {
    // State
    undoModeEnabled,
    hasUndoPending,
    undoPendingMessages,
    lastPendingMessageId,

    // Actions
    undoLastMessage,
    undoMessage,
    sendWithUndo,

    // Lifecycle helpers (for manual control)
    setupUndoHotkeys,
    cleanupUndoHotkeys,
  };
}
